import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Button, createStyles, Icon, Input, Stack, TextInput, TFText, Title, Transition } from '@tf/ui';
import { isValidEmail } from '@tf/utils';
import type React from 'react';
import { useState } from 'react';

const useStyles = createStyles(() => ({
	form: {
		width: '100%',
	},
}));

interface Props {
	isLoading: boolean;
	onLogin: (payload: { email: string }) => Promise<any>;
}

export const SignUpForm: React.FC<Props> = ({ isLoading, onLogin }) => {
	const { classes } = useStyles();
	const { _ } = useLingui();

	const [step, setStep] = useState<'FORM' | 'SUCCESS'>('FORM');
	const [email, setEmail] = useState('');
	const [isValid, setIsValid] = useState(true);
	const [serverError, setServerError] = useState<string | null>(null);

	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!isValidEmail(email)) {
			setIsValid(false);
			return;
		}

		try {
			await onLogin({ email });
		} catch (err) {
			if (err instanceof Response) {
				const body = await err.json();
				if (body?.code === 'ORIGIN_TENANT_MISMATCH') {
					setServerError(
						_(
							msg({
								id: 'auth.loginForm.emailAlreadyInUse',
								message: 'This email address is already in use. Please use a different email.',
							}),
						),
					);
					return;
				}
			}
			throw err;
		}
		setStep('SUCCESS');
	};

	return (
		<Stack w="100%" gap="12px" align="center">
			{step === 'FORM' ? (
				<Title m="12px 0" lh="32px" fz="24px" fw="600" order={2}>
					<Trans id="auth.loginForm.titleForm">Create an account</Trans>
				</Title>
			) : (
				<Stack mt="12px" align="center">
					<Icon.IconCircleCheck color="#2563EB" size="36px" stroke="1.5" />
					<Title fz="18px" lh="20px" fw="600">
						<Trans id="auth.loginForm.titleLinkSent">Check your email</Trans>
					</Title>
				</Stack>
			)}

			{step === 'FORM' ? (
				<form className={classes.form} noValidate onSubmit={handleLogin}>
					<Stack gap="16px">
						<Input.Wrapper
							id="email"
							label={_(msg({ id: 'auth.loginForm.emailFieldLabel', message: 'Email address' }))}
						>
							<TextInput
								h="34px"
								size="sm"
								id="email"
								name="email"
								type="email"
								error={!isValid || !!serverError}
								value={email}
								onChange={(e) => {
									setIsValid(true);
									setServerError(null);
									setEmail(e.target.value);
								}}
							/>
							<TFText fz="12px" lh="18px" mt="4px" c={!isValid || serverError ? '#fa5252' : '#6B7280'}>
								{serverError ? (
									serverError
								) : !isValid ? (
									<Trans id="auth.loginForm.invalidEmail">Please enter a valid email</Trans>
								) : (
									<Trans id="auth.loginForm.magicLinkExplanation">
										You’ll receive a confirmation link to your email
									</Trans>
								)}
							</TFText>
						</Input.Wrapper>

						<Button type="submit" fullWidth loading={isLoading} sx={{ height: '42px' }}>
							<TFText size="md">
								<Trans id="auth.loginForm.submitButton">Get started</Trans>
							</TFText>
						</Button>
					</Stack>
				</form>
			) : null}

			<Transition mounted={step === 'SUCCESS'} transition="pop" duration={350} exitDuration={0}>
				{(styles) => (
					<Stack w="100%" align="center" style={styles}>
						<TFText ta="center" fz="14px" lh="20px" w="100%" c="#6B7280">
							<Trans id="auth.loginForm.linkSentMessage">We’ve sent a verification link to {email}</Trans>
						</TFText>

						<Button c="#2563EB" mt="12px" fullWidth variant="light" onClick={() => setStep('FORM')}>
							<Trans id="auth.loginForm.newRequestButton">Request a new link</Trans>
						</Button>
					</Stack>
				)}
			</Transition>
		</Stack>
	);
};
